import React from "react";

interface LoaderProps {
  size?: "sm" | "md" | "lg" | "xl";
  text?: string;
  fullScreen?: boolean;
  showText?: boolean;
}

const DataLoader: React.FC<LoaderProps> = ({
  size = "md",
  text = "Loading",
  fullScreen = false,
  showText = true,
}) => {
  // Size configurations
  const sizeClasses: Record<
    string,
    { container: string; text: string; orbit: string; center: string }
  > = {
    sm: {
      container: "w-16 h-16",
      text: "text-xs",
      orbit: "w-20 h-20",
      center: "w-6 h-7",
    },
    md: {
      container: "w-24 h-24",
      text: "text-sm",
      orbit: "w-28 h-28",
      center: "w-10 h-10",
    },
    lg: {
      container: "w-32 h-32",
      text: "text-base",
      orbit: "w-40 h-40",
      center: "w-12 h-14",
    },
    xl: {
      container: "w-48 h-48",
      text: "text-lg",
      orbit: "w-56 h-56",
      center: "w-16 h-20",
    },
  };

  // Solar Orbit Loader
  const SolarOrbit = () => (
    <div className="relative flex items-center justify-center">
      <div className={`${sizeClasses[size].orbit} relative`}>
        {/* Sun (center) */}
        <div
          className={`absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 ${sizeClasses[size].center} bg-gradient-to-br from-blue-100 via-blue-200 to-blue-300 rounded-xl border border-blue-300 shadow-2xl relative overflow-hidden`}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-blue-400/20 via-transparent to-blue-600/20 animate-pulse"></div>
          <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-blue-100/30 to-transparent animate-[spin_4s_linear_infinite]"></div>
        </div>

        {/* Mercury orbit */}
        <div
          className="absolute inset-4 animate-spin"
          style={{ animationDuration: "1.5s" }}
        >
          <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-2 h-2 bg-blue-600 rounded-full shadow-lg"></div>
        </div>

        {/* Venus orbit */}
        <div
          className="absolute inset-2 animate-spin"
          style={{ animationDuration: "2.5s" }}
        >
          <div className="absolute right-0 top-1/2 transform translate-x-1/2 -translate-y-1/2 w-2.5 h-2.5 bg-blue-500 rounded-full shadow-lg"></div>
        </div>

        {/* Earth orbit with moon */}
        <div
          className="absolute inset-0 animate-spin"
          style={{ animationDuration: "4s" }}
        >
          <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 w-3 h-3 bg-blue-400 rounded-full shadow-lg relative">
            <div
              className="absolute -top-1 -right-1 w-1 h-1 bg-blue-300 rounded-full animate-spin"
              style={{ animationDuration: "0.8s" }}
            ></div>
          </div>
        </div>

        {/* Mars orbit */}
        <div
          className="absolute -inset-2 animate-spin"
          style={{ animationDuration: "6s", animationDirection: "reverse" }}
        >
          <div className="absolute left-0 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-2.5 h-2.5 bg-blue-300 rounded-full shadow-lg animate-pulse"></div>
        </div>

        {/* Asteroid belt */}
        <div
          className="absolute -inset-1 animate-spin"
          style={{ animationDuration: "8s" }}
        >
          <div className="absolute top-1/4 right-0 transform translate-x-1/2 -translate-y-1/2 w-1 h-1 bg-blue-200 rounded-full"></div>
          <div className="absolute bottom-1/4 right-1/4 w-1 h-1 bg-blue-200 rounded-full"></div>
          <div className="absolute bottom-1/4 left-1/4 w-1 h-1 bg-blue-200 rounded-full"></div>
          <div className="absolute top-1/4 left-0 w-1 h-1 bg-blue-200 rounded-full"></div>
        </div>

        {/* Orbit rings */}
        <div className="absolute inset-4 border border-blue-100/40 rounded-full"></div>
        <div className="absolute inset-2 border border-blue-100/30 rounded-full"></div>
        <div className="absolute inset-0 border border-blue-100/20 rounded-full"></div>
        <div className="absolute -inset-2 border border-blue-100/10 rounded-full"></div>
      </div>
    </div>
  );

  const LoaderContent = () => (
    <div className="flex flex-col items-center justify-center space-y-8">
      {SolarOrbit()}
      {showText && (
        <div
          className={`${sizeClasses[size].text} font-medium tracking-wide text-blue-600`}
        >
          <span>{text}</span>
          <span className="inline-flex ml-1">
            <span className="animate-pulse">.</span>
            <span className="animate-pulse" style={{ animationDelay: "0.2s" }}>
              .
            </span>
            <span className="animate-pulse" style={{ animationDelay: "0.4s" }}>
              .
            </span>
          </span>
        </div>
      )}
    </div>
  );

  if (fullScreen) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center z-50">
        <LoaderContent />
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center p-12">
      <LoaderContent />
    </div>
  );
};

export default DataLoader;